import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { useAuth } from "../../utils/AuthContext"; // Assuming you have an auth context
import { url } from "../../Constant/TextConstant";
import Header from "../Header";
import { Helmet } from "react-helmet";
import { useUserLocation } from "../../utils/useUserLocation";

const MyRewardScreen = () => {
  const [rewardPoints, setRewardPoints] = useState(0);
  const [rewardHistory, setRewardHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = useAuth();
  const baseAPIUrl = url.baseAPIUrl;
  // Replace the existing location state with the useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });
 useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      // Update location through the hook's update function
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };
  useEffect(() => {
    if (!auth.currentUser) {
      setError("User is not authenticated");
      return;
    }

    const fetchRewardData = async () => {
      try {
        setLoading(true);
        const [pointsResponse, historyResponse] = await Promise.all([
          axios.get(
            `${baseAPIUrl}/api/reward/GetMyReward?customerMobile=${auth.currentUser}`,
            {
              headers: { Accept: "application/json" },
            }
          ),
          axios.get(
            `${baseAPIUrl}/api/reward/getCashbackDetailsForCustomer?customerMobile=${auth.currentUser}`,
            {
              headers: { Accept: "application/json" },
            }
          ),
        ]);

        setRewardPoints(pointsResponse.data);
        setRewardHistory(historyResponse.data);
      } catch (err) {
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchRewardData();
  }, [auth.currentUser]);

  if (loading) {
    return (
      <>
        {" "}
        <Helmet>
          <title>Loading Rewards | MyStore</title>
          <meta
            name="description"
            content="Loading your reward points and history"
          />
        </Helmet>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {" "}
        <Helmet>
          <title>Error Loading Rewards | MyStore</title>
          <meta
            name="description"
            content="Error occurred while loading your rewards"
          />
        </Helmet>
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
        </ErrorContainer>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`My Rewards - ${rewardPoints} Points | MyStore`}</title>
        <meta
          name="description"
          content={`View your ${rewardPoints} reward points and cashback history. Redeem your points for exciting offers.`}
        />
        <meta
          name="keywords"
          content="rewards, loyalty points, cashback, discounts, MyStore rewards"
        />
        <meta
          property="og:title"
          content={`My Rewards - ${rewardPoints} Points | MyStore`}
        />
        <meta
          property="og:description"
          content={`Check out your ${rewardPoints} reward points and cashback history on MyStore`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${window.location.origin}/rewards-og-image.jpg`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`My Rewards - ${rewardPoints} Points | MyStore`}
        />
        <meta
          name="twitter:description"
          content={`View your ${rewardPoints} reward points and cashback history`}
        />
        <meta
          name="twitter:image"
          content={`${window.location.origin}/rewards-twitter-image.jpg`}
        />
        <link rel="canonical" href={window.location.href} />
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "LoyaltyProgram",
            "name": "MyStore Rewards",
            "description": "Customer loyalty program offering points and cashback",
            "url": "${window.location.href}",
            "member": {
              "@type": "Person",
              "name": "Customer"
            },
            "programName": "MyStore Rewards Program",
            "points": ${rewardPoints}
          }`}
        </script>
      </Helmet>

      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <Container>
        <AppBar>
          <AppBarTitle>Reward</AppBarTitle>
        </AppBar>

        <Content>
          {/* Reward points section */}
          <RewardSection>
            <RewardImage
              src="https://nearmeind.com/img/rewardCoin.png"
              alt="Reward Coin"
            />
            <RewardPoints>{rewardPoints}</RewardPoints>
            <RewardLabel>Coins</RewardLabel>
          </RewardSection>

          {/* Reward history section */}
          <HistorySection>
            {rewardHistory.map((item, index) => (
              <HistoryCard key={index}>
                <StoreName>{item.StoreName || "Unknown Store"}</StoreName>
                <OrderDate>{item.LastOrderDate || "Unknown Date"}</OrderDate>
                <Cashback>{item.Cashback} Coins</Cashback>
              </HistoryCard>
            ))}
          </HistorySection>
        </Content>
      </Container>
    </>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const AppBar = styled.div`
  background-color: #4caf50; /* mainColor equivalent */
  color: white;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* topBarElevation equivalent */
`;

const AppBarTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  color: white; /* topBarWhiteColor equivalent */
`;

const Content = styled.div`
  flex: 1;
  padding: 16px;
`;

const RewardSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  text-align: center;
`;

const RewardImage = styled.img`
  width: 50%;
  max-width: 200px;
  margin-bottom: 20px;
`;

const RewardPoints = styled.h2`
  font-size: 4rem;
  font-weight: bold;
  color: #333;
  font-family: "Georgia", serif;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const RewardLabel = styled.p`
  font-size: 1.5rem;
  font-weight: 500;
  color: #333;
  font-family: "Georgia", serif;
  margin: 10px 0 0;
`;

const HistorySection = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const HistoryCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  margin: 8px 16px;
  padding: 16px;
  position: relative;
`;

const StoreName = styled.h3`
  font-weight: bold;
  margin: 0 0 8px;
`;

const OrderDate = styled.p`
  color: #666;
  margin: 0 0 8px;
`;

const Cashback = styled.p`
  font-weight: bold;
  color: #008080; /* teal color */
  margin: 0;
  position: absolute;
  right: 16px;
  top: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #4caf50;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  padding: 20px;
`;

const ErrorText = styled.p`
  color: #d32f2f;
  font-size: 1.2rem;
  text-align: center;
`;

export default MyRewardScreen;
